import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiFileText, FiPlus, FiTrash2, FiCheck } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import Modal from './Modal';
import { appAlert, appConfirm } from '../utils/appDialog';

const LAYOUT_OPTIONS = [
  { value: 'classic', label: 'Classic Professional' },
  { value: 'modern', label: 'Modern Clean' },
  { value: 'compact', label: 'Compact ATS' },
];

const emptyForm = () => ({
  name: '',
  description: '',
  layout_key: 'classic',
  is_default: false,
  is_active: true,
  accent: '#1e3a5f',
  font_size: 10,
  margin: 48,
});

/**
 * Settings → Resume templates used when generating PDF/DOCX from candidate profiles.
 */
export default function ResumeTemplatesSettings() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const { data: templates = [], isLoading } = useQuery(['resume-templates-admin'], () =>
    api.get('/resume-templates?all=1').then((r) => r.data)
  );

  const saveMutation = useMutation(
    async () => {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        layout_key: form.layout_key,
        is_default: form.is_default,
        is_active: form.is_active,
        config: {
          accent: form.accent || '#1e3a5f',
          font_size: Number(form.font_size) || 10,
          margin: Number(form.margin) || 48,
          show_skills_inline: true,
          header_bar: form.layout_key === 'modern',
          compact: form.layout_key === 'compact',
        },
      };
      if (editingId) {
        return api.put(`/resume-templates/${editingId}`, payload);
      }
      return api.post('/resume-templates', payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['resume-templates-admin']);
        queryClient.invalidateQueries(['resume-templates']);
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm());
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteMutation = useMutation((id) => api.delete(`/resume-templates/${id}`), {
    onSuccess: () => {
      queryClient.invalidateQueries(['resume-templates-admin']);
      queryClient.invalidateQueries(['resume-templates']);
    },
    onError: (e) => void appAlert(e.response?.data?.error || e.message),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      name: t.name || '',
      description: t.description || '',
      layout_key: t.layout_key || 'classic',
      is_default: Boolean(t.is_default),
      is_active: t.is_active !== false,
      accent: t.config?.accent || '#1e3a5f',
      font_size: t.config?.font_size || 10,
      margin: t.config?.margin || 48,
    });
    setShowModal(true);
  };

  return (
    <div className="settings-section">
      <h2>
        <FiFileText /> Resume templates
      </h2>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Configure layouts used when generating a PDF or DOCX resume from a candidate profile.
        Staff can download from Candidate Details → Resume details.
      </p>

      <div style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <FiPlus /> Add template
        </button>
      </div>

      {isLoading ? (
        <div className="loading">Loading…</div>
      ) : templates.length === 0 ? (
        <p className="form-hint">
          No templates yet. Add Classic, Modern, or Compact layouts for profile export.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Layout</th>
                <th>Accent</th>
                <th>Default</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.name}</strong>
                    {t.description ? (
                      <div className="form-hint" style={{ margin: 0 }}>
                        {t.description}
                      </div>
                    ) : null}
                  </td>
                  <td>{t.layout_key}</td>
                  <td>
                    <span
                      className="resume-tpl-swatch"
                      style={{ background: t.config?.accent || '#1e3a5f' }}
                      title={t.config?.accent}
                    />
                  </td>
                  <td>{t.is_default ? <FiCheck aria-label="Default" /> : '—'}</td>
                  <td>{t.is_active ? 'Yes' : 'No'}</td>
                  <td className="table-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => openEdit(t)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      aria-label={`Delete ${t.name}`}
                      onClick={async () => {
                        if (await appConfirm(`Delete template “${t.name}”?`)) {
                          deleteMutation.mutate(t.id);
                        }
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingId(null);
        }}
        title={editingId ? 'Edit resume template' : 'Add resume template'}
      >
        <div className="form-group">
          <label htmlFor="rt-name">Name</label>
          <input
            id="rt-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Classic Professional"
          />
        </div>
        <div className="form-group">
          <label htmlFor="rt-desc">Description</label>
          <input
            id="rt-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="rt-layout">Layout</label>
            <select
              id="rt-layout"
              value={form.layout_key}
              onChange={(e) => setForm({ ...form, layout_key: e.target.value })}
            >
              {LAYOUT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="rt-accent">Accent color</label>
            <input
              id="rt-accent"
              type="color"
              value={form.accent}
              onChange={(e) => setForm({ ...form, accent: e.target.value })}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="rt-font">Font size (pt)</label>
            <input
              id="rt-font"
              type="number"
              min={8}
              max={14}
              value={form.font_size}
              onChange={(e) => setForm({ ...form, font_size: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="rt-margin">Margin (pt)</label>
            <input
              id="rt-margin"
              type="number"
              min={24}
              max={72}
              value={form.margin}
              onChange={(e) => setForm({ ...form, margin: e.target.value })}
            />
          </div>
        </div>
        <div className="form-group" style={{ display: 'flex', gap: '1.25rem' }}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.is_default}
              onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
            />{' '}
            Default template
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />{' '}
            Active
          </label>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowModal(false);
              setEditingId(null);
            }}
          >
            Cancel
          </button>
          <LoadingButton
            type="button"
            className="btn btn-primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
            disabled={!form.name.trim()}
          >
            {editingId ? 'Save' : 'Create'}
          </LoadingButton>
        </div>
      </Modal>

      <style>{`
        .resume-tpl-swatch {
          display: inline-block;
          width: 1.1rem;
          height: 1.1rem;
          border-radius: 4px;
          border: 1px solid var(--workspace-border, #ccc);
          vertical-align: middle;
        }
        .table-actions {
          display: flex;
          gap: 0.35rem;
          justify-content: flex-end;
        }
        .checkbox-label {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: var(--text-sm, 0.875rem);
        }
      `}</style>
    </div>
  );
}
